
import React, { useState, useCallback, useRef, useEffect, useId, useMemo } from 'react';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import Image from '@/components/AppImage/AppImage';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { toast } from 'react-toastify';
import { debug, error as logError } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_ENRICHED_REC_MODAL_OPENED,
  EVENT_ENRICHED_REC_SUBMITTED,
  EVENT_ENRICHED_REC_EDITED,
  EVENT_ENRICHED_REC_VIEW_MODAL_DISMISSED,
} from '@/lib/analyticsEvents';
import {
  createRecommendation,
  patchRecommendation,
  normalizePublicRecommendationResponse,
  type EnrichedRecommendationDoc,
  type EnrichedRecommendationItem,
  type EnrichedRecommendationPayload,
  type PublicRecommendationResponse,
} from '@/api/enrichedRecommendation';
import { useMutation } from '@/hooks/useApi';
import { uploadRecommendationPhoto } from '@/lib/s3Client';
import { useAuth } from '@/contexts';
import type Location from '@/models/location';
import type { Location as CombinedPlaceLocation } from '@/models/locationModel';
import type { PlaceCardData } from '@/types/listViewTypes';
import {
  getUserItineraries,
  getUserLists,
  addPlaceToItinerary,
  addPlaceToList,
  type SavedItinerary,
} from '@/api/itinerary';
import SaveToOverlay from '@/components/SaveToOverlay/SaveToOverlay';
import CombinedContentPlaceModal from '@/components/CombinedContentPlaceModal/CombinedContentPlaceModal';
import PlaceModalPhotosTab from '@/components/CombinedContentPlaceModal/PlaceModalPhotosTab';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import CustomImage from '@/components/CustomImage/CustomImage';
import { DEFAULT_POST_IMAGE } from '@/constants/common';
import { getFirstPhotoUrl, type PhotoIdentifier } from '@/utils/photoUtils';
import { RECOMMENDATION_TRAVEL_TAGS } from '@/constants/recommendationTags';
import { preferencePillClass, preferencePillStyle } from '@/constants/preferencePillClass';
import { RecommendationPreferenceTagIcon } from '@/constants/tripTypes';
import { getCanonicalLocationMongoId } from '@/utils/canonicalLocationId';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import { extractPhotoUrl } from '@/utils/locationDataExtractor';
import { useKeyboardAwareContainer } from '@/hooks/useKeyboardAwareContainer';
import './recommendModalStacking.css';

/** Matches backend `recommendation_payload` (max 10 HTTPS URLs per entry). */
const MAX_PHOTOS = 10;
/** Backend allows max 2000 characters for `notes`. */
const MAX_NOTES_LENGTH = 2000;
/** Backend allows only `https://` for `external_link` (and each `photo_urls` entry). */
const EXTERNAL_LINK_PREFIX = 'https://';

function isValidExternalLink(value: string): boolean {
  if (!value) return true;
  return value.startsWith(EXTERNAL_LINK_PREFIX);
}

function onlyHttpsPhotoUrls(urls: string[]): string[] {
  return urls.filter((u) => typeof u === 'string' && u.startsWith('https://')).slice(0, MAX_PHOTOS);
}

function buildFullRecommendationPayload(
  notes: string,
  selectedTags: Set<string>,
  photoUrls: string[],
  externalLink: string,
): EnrichedRecommendationPayload {
  const trimmedNotes = notes.trim();
  const trimmedLink = externalLink.trim();
  const httpsPhotos = onlyHttpsPhotoUrls(photoUrls);
  return {
    notes: trimmedNotes === '' ? null : trimmedNotes,
    travel_tags: Array.from(selectedTags),
    photo_urls: httpsPhotos,
    external_link: trimmedLink === '' ? '' : trimmedLink,
  };
}

/** Same extraction chain as CombinedContentPlaceModal / place cards (main_photo, image_urls, Google ids). */
function getLocationPhoto(location: Location): string | null {
  const url = extractPhotoUrl(
    location as unknown as Parameters<typeof extractPhotoUrl>[0],
    600,
  );
  return url.trim() ? url : null;
}

function formatGoogleTypeLabel(raw: string): string {
  return raw
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getGoogleTypePills(location: Location): string[] {
  const raw =
    (location.google_places_data as { types?: string[]; primaryType?: string } | undefined)
      ?.types ??
    (location as unknown as { google_types?: string[] }).google_types ??
    [];
  return raw.slice(0, 2).map(formatGoogleTypeLabel);
}

// ─── Props ───────────────────────────────────────────────────────────────────

type ViewLoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; data: PublicRecommendationResponse };

export type RecommendThisPlaceModalProps =
  | {
      isOpen: boolean;
      onClose: () => void;
      mode: 'create' | 'edit';
      location: Location;
      /** Required when mode === 'edit' */
      initialRecommendation?: EnrichedRecommendationDoc;
      /** Called with the saved doc so the caller can update its cache */
      onRecommended?: (rec: EnrichedRecommendationDoc) => void;
      /**
       * When true the hero image is non-interactive (no click to open CombinedContentPlaceModal).
       * Use when this modal is opened from inside CombinedContentPlaceModal — the user can already
       * see the place details behind the sheet and clicking the hero would cause a stacking conflict.
       */
      hidePlaceLink?: boolean;
      /**
       * After create, skip SaveToOverlay. Parent should advance from onRecommended
       * (e.g. Create New Link success) and unmount this modal; we do not call onClose.
       */
      skipSaveOverlay?: boolean;
      /** Primary CTA label in create mode (default: Share). */
      submitLabel?: string;
    }
  | {
      isOpen: boolean;
      onClose: () => void;
      mode: 'view';
      loadState: ViewLoadState;
      onRetry?: () => void;
      /** Mixpanel `source` on view-mode open/dismiss (default: profile share landing) */
      viewEntrySource?: 'profile_share_landing';
      /**
       * Share landing: once loaded, skip the recommendation sheet and show place details
       * (CombinedContentPlaceModal) directly. Falls back to the sheet when the place is gone.
       */
      openPlaceDetailsDirectly?: boolean;
    };

// ─── Component ───────────────────────────────────────────────────────────────

const RecommendThisPlaceModal = (props: RecommendThisPlaceModalProps) => {
  const { isOpen, onClose, mode } = props;
  const { user } = useAuth();

  // Discriminated prop extraction
  const location = props.mode !== 'view' ? props.location : undefined;
  const initialRecommendation = props.mode === 'edit' ? props.initialRecommendation : undefined;
  const onRecommended = props.mode !== 'view' ? props.onRecommended : undefined;
  const viewLoadState = props.mode === 'view' ? props.loadState : undefined;
  const onRetry = props.mode === 'view' ? props.onRetry : undefined;
  const viewEntrySource =
    props.mode === 'view' ? (props.viewEntrySource ?? 'profile_share_landing') : undefined;
  const openPlaceDetailsDirectly =
    props.mode === 'view' ? (props.openPlaceDetailsDirectly ?? false) : false;
  const hidePlaceLink = props.mode !== 'view' ? (props.hidePlaceLink ?? false) : false;
  const skipSaveOverlay = props.mode !== 'view' ? (props.skipSaveOverlay ?? false) : false;
  const submitLabel =
    props.mode !== 'view' ? (props.submitLabel ?? 'Share') : 'Share';

  // Normalize view data once per load
  const viewNormalized = useMemo(() => {
    if (!viewLoadState || viewLoadState.status !== 'loaded') return null;
    return normalizePublicRecommendationResponse(viewLoadState.data);
  }, [viewLoadState]);

  /** Recommendation `photo_urls` only (excludes hero fallback from location). */
  const viewUserPhotoUrls = useMemo(() => {
    if (mode !== 'view' || !viewNormalized?.recommendation?.photo_urls) return [];
    return viewNormalized.recommendation.photo_urls.filter(
      (u): u is string => typeof u === 'string' && u.trim().length > 0,
    );
  }, [mode, viewNormalized]);

  // ── Derived display values (used in JSX for all modes) ──────────────────
  const locationId =
    mode !== 'view'
      ? (() => {
          const raw = getCanonicalLocationMongoId({
            _id: (location as unknown as { _id?: string })?._id,
            location_id: (location as unknown as { location_id?: string })?.location_id,
          });
          return raw && isMongoObjectIdString(raw) ? raw : undefined;
        })()
      : (viewNormalized?.location as unknown as { _id?: string } | null)?._id ?? undefined;

  const locationName =
    mode !== 'view'
      ? (location?.name ?? 'This Place')
      : (viewNormalized?.location?.name ?? 'Place unavailable');

  const heroPhotoSrc: string | null =
    mode !== 'view'
      ? getLocationPhoto(location as Location)
      : (() => {
          const rec = viewNormalized?.recommendation;
          const loc = viewNormalized?.location;
          return (
            (rec?.photo_urls?.[0] ?? null) ??
            (loc
              ? getFirstPhotoUrl(
                  {
                    photos: (loc as unknown as { photos?: PhotoIdentifier[] }).photos,
                    google_places_data: {
                      photos: (loc as unknown as { photos?: PhotoIdentifier[] }).photos,
                    },
                  },
                  800,
                )
              : null) ??
            DEFAULT_POST_IMAGE
          );
        })();

  const typePills =
    mode !== 'view'
      ? getGoogleTypePills(location as Location)
      : (
          (viewNormalized?.location as unknown as { google_places_data?: { types?: string[] } } | null)
            ?.google_places_data?.types ??
          (viewNormalized?.location as unknown as { google_types?: string[] } | null)?.google_types ??
          []
        )
          .slice(0, 2)
          .map(formatGoogleTypeLabel);

  const locationData: Partial<CombinedPlaceLocation> | undefined =
    mode !== 'view'
      ? (location as unknown as Partial<CombinedPlaceLocation>)
      : (viewNormalized?.location as unknown as Partial<CombinedPlaceLocation> | undefined);

  // ── Form state (create/edit only — hooks must be unconditional) ──────────
  const [notes, setNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [externalLink, setExternalLink] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [saveOverlayOpen, setSaveOverlayOpen] = useState(false);
  const [userItineraries, setUserItineraries] = useState<SavedItinerary[]>([]);
  const [isLoadingItineraries, setIsLoadingItineraries] = useState(false);
  const [userLists, setUserLists] = useState<SavedItinerary[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [selectedPlaceForSave, setSelectedPlaceForSave] = useState<PlaceCardData | null>(null);
  const [placeDetailsOpen, setPlaceDetailsOpen] = useState(false);
  const [viewPhotosOpen, setViewPhotosOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const { containerRef: scrollBodyRef, setContainerRef: setScrollBodyRef, scrollFocusedIntoView, blurActiveInput } =
    useKeyboardAwareContainer({ enabled: isOpen });

  const trackedOpenRef = useRef(false);
  const trackedViewOpenRef = useRef(false);
  const closeIconClipId = useId().replace(/:/g, '');

  // ── SaveToOverlay data fetchers ──────────────────────────────────────────
  const fetchUserItinerariesForSave = useCallback(async () => {
    if (!user) return;
    setIsLoadingItineraries(true);
    try {
      const response = await getUserItineraries({ page: 1, limit: 50, includeDrafts: false, includePrivate: false });
      setUserItineraries(response.posts);
    } catch {
      setUserItineraries([]);
    } finally {
      setIsLoadingItineraries(false);
    }
  }, [user]);

  const fetchUserListsForSave = useCallback(async () => {
    if (!user) return;
    setIsLoadingLists(true);
    try {
      const response = await getUserLists({ page: 1, limit: 50, includeDrafts: false, includePrivate: false });
      setUserLists(response.posts);
    } catch {
      setUserLists([]);
    } finally {
      setIsLoadingLists(false);
    }
  }, [user]);

  const handleSaveToItinerary = useCallback(
    async (itineraryId: string) => {
      if (!selectedPlaceForSave) throw new Error('No place selected');
      await addPlaceToItinerary(itineraryId, selectedPlaceForSave);
    },
    [selectedPlaceForSave],
  );

  const handleSaveToList = useCallback(
    async (listId: string) => {
      if (!selectedPlaceForSave) throw new Error('No place selected');
      await addPlaceToList(listId, selectedPlaceForSave);
    },
    [selectedPlaceForSave],
  );

  const handleNewTripOrListCreated = useCallback(() => {
    void fetchUserItinerariesForSave();
    void fetchUserListsForSave();
  }, [fetchUserItinerariesForSave, fetchUserListsForSave]);

  // Pre-fill when editing
  useEffect(() => {
    if (!isOpen || mode === 'view') return;
    if (mode === 'edit' && initialRecommendation) {
      setNotes(initialRecommendation.notes ?? '');
      setSelectedTags(new Set(initialRecommendation.travel_tags ?? []));
      setPhotoUrls(initialRecommendation.photo_urls ?? []);
      setExternalLink(initialRecommendation.external_link ?? '');
    } else if (mode === 'create') {
      setNotes('');
      setSelectedTags(new Set());
      setPhotoUrls([]);
      setExternalLink('');
    }
    setLinkError(null);
  }, [isOpen, mode, initialRecommendation]);

  useEffect(() => {
    if (!isOpen) {
      setPlaceDetailsOpen(false);
      setViewPhotosOpen(false);
    }
  }, [isOpen]);

  const requestCloseView = useCallback(
    (dismissMethod: 'backdrop' | 'close' | 'escape') => {
      blurActiveInput();
      if (mode === 'view' && trackedViewOpenRef.current && viewNormalized?.recommendation) {
        const rec = viewNormalized.recommendation;
        WebAnalytics.track(EVENT_ENRICHED_REC_VIEW_MODAL_DISMISSED, {
          dismiss_method: dismissMethod,
          location_id: rec.location_id ?? locationId,
          recommendation_id: rec._id,
          source: viewEntrySource ?? 'profile_share_landing',
        });
      }
      onClose();
    },
    [mode, onClose, viewNormalized, locationId, viewEntrySource, blurActiveInput],
  );

  const handleClose = useCallback(() => {
    blurActiveInput();
    onClose();
  }, [blurActiveInput, onClose]);

  const handleOverlayClose = useCallback(() => {
    if (mode === 'view') requestCloseView('backdrop');
    else handleClose();
  }, [mode, requestCloseView, handleClose]);

  // Analytics: track modal open once per open (create/edit only)
  useEffect(() => {
    if (mode === 'view') return;
    if (isOpen && !trackedOpenRef.current) {
      trackedOpenRef.current = true;
      console.log('[RecommendThisPlaceModal] opened', { mode, locationId });
      WebAnalytics.track(EVENT_ENRICHED_REC_MODAL_OPENED, {
        mode,
        location_id: locationId,
        source: hidePlaceLink ? 'place_modal' : 'list_card',
      });
    }
    if (!isOpen) {
      trackedOpenRef.current = false;
    }
  }, [isOpen, mode, locationId, hidePlaceLink]);

  // Analytics: view mode (shared rec) — open once when public payload is ready
  useEffect(() => {
    if (mode !== 'view' || !isOpen || viewLoadState?.status !== 'loaded' || !viewNormalized?.recommendation) {
      return;
    }
    if (trackedViewOpenRef.current) return;
    trackedViewOpenRef.current = true;
    const rec = viewNormalized.recommendation;
    WebAnalytics.track(EVENT_ENRICHED_REC_MODAL_OPENED, {
      mode: 'view',
      location_id: rec.location_id ?? locationId,
      recommendation_id: rec._id,
      source: viewEntrySource ?? 'profile_share_landing',
    });
  }, [mode, isOpen, viewLoadState?.status, viewNormalized, locationId, viewEntrySource]);

  useEffect(() => {
    if (!isOpen && mode === 'view') {
      trackedViewOpenRef.current = false;
    }
  }, [isOpen, mode]);

  /** On open: focus the notes field once the sheet's slide-up animation settles.
   * If this fires after the scroll-to-bottom effect below has already scrolled
   * down, the field's own onFocus/focusin handlers re-scroll it into view. */
  useEffect(() => {
    if (!isOpen || mode === 'view') return;
    const t = window.setTimeout(() => {
      notesRef.current?.focus();
    }, 500);
    return () => window.clearTimeout(t);
  }, [isOpen, mode]);

  /**
   * On open: scroll to bottom so Share/Save is visible when content overflows.
   * Skipped when keyboard is open or an editable field already has focus.
   */
  useEffect(() => {
    if (!isOpen || mode === 'view') return;
    let cancelled = false;

    const scrollToBottomIfNeeded = () => {
      if (cancelled) return;
      if (document.documentElement.hasAttribute('data-keyboard-open')) return;
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')
      ) {
        return;
      }
      const el = scrollBodyRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 2) return;
      el.scrollTop = el.scrollHeight;
    };

    const rafOuter = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToBottomIfNeeded);
    });
    const t1 = window.setTimeout(scrollToBottomIfNeeded, 80);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafOuter);
      window.clearTimeout(t1);
    };
  }, [isOpen, mode, scrollBodyRef]);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const handlePhotoFilesChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (!files.length) return;

      const remaining = MAX_PHOTOS - photoUrls.length;
      if (remaining <= 0) {
        toast.warn(`Maximum ${MAX_PHOTOS} photos allowed.`);
        return;
      }

      const toUpload = files.slice(0, remaining);
      if (!locationId) {
        toast.error('Location ID is missing. Please try again.');
        return;
      }
      setUploadingPhotos(true);
      debug('[RecommendThisPlaceModal] Uploading photos', { count: toUpload.length });

      try {
        const uploaded = await Promise.all(
          toUpload.map((f) => uploadRecommendationPhoto(f, locationId)),
        );
        const invalid = uploaded.filter((u) => !u.startsWith('https://'));
        if (invalid.length > 0) {
          logError('[RecommendThisPlaceModal] Non-HTTPS photo URL from upload', { invalid });
          toast.error('Photos must be HTTPS URLs. Please try again or contact support.');
          return;
        }
        setPhotoUrls((prev) => [...prev, ...uploaded].slice(0, MAX_PHOTOS));
        debug('[RecommendThisPlaceModal] Photos uploaded', { urls: uploaded });
      } catch (err) {
        logError('[RecommendThisPlaceModal] Photo upload failed', { error: err });
        toast.error('Photo upload failed. Please try again.');
      } finally {
        setUploadingPhotos(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [locationId, photoUrls.length],
  );

  const removePhoto = useCallback((url: string) => {
    setPhotoUrls((prev) => prev.filter((u) => u !== url));
  }, []);

  const validateLink = useCallback((value: string): boolean => {
    if (!value) {
      setLinkError(null);
      return true;
    }
    if (!isValidExternalLink(value)) {
      setLinkError('Link must start with https://');
      return false;
    }
    setLinkError(null);
    return true;
  }, []);

  const linkBlocksSubmit =
    externalLink.trim().length > 0 && !isValidExternalLink(externalLink.trim());

  const { mutate: upsertMutate, loading: upsertLoading } = useMutation<EnrichedRecommendationItem>(
    (payload: unknown) => {
      if (!locationId) {
        return Promise.reject(new Error('Missing location id'));
      }
      return createRecommendation(locationId, payload as EnrichedRecommendationPayload);
    },
    { showErrorToast: true },
  );

  const { mutate: patchMutate, loading: patchLoading } = useMutation<EnrichedRecommendationDoc>(
    (args: unknown) => {
      const { id, partial } = args as {
        id: string;
        partial: Partial<EnrichedRecommendationPayload>;
      };
      return patchRecommendation(id, partial);
    },
    { showErrorToast: true },
  );

  const isSubmitting = upsertLoading || patchLoading || uploadingPhotos;

  const handleSubmit = useCallback(async () => {
    if (mode === 'view') return;
    if (externalLink && !validateLink(externalLink)) return;
    if (!locationId) {
      debug('[RecommendThisPlaceModal] submit blocked: empty canonical location id', {
        mode,
        locationName: location?.name,
        rawId: (location as unknown as { _id?: string })?._id,
        rawLocationId: (location as unknown as { location_id?: string })?.location_id,
      });
      toast.error('Location ID is missing. Please try again.');
      return;
    }

    const payload = buildFullRecommendationPayload(notes, selectedTags, photoUrls, externalLink);

    try {
      let saved: EnrichedRecommendationDoc;

      if (mode === 'edit' && initialRecommendation) {
        saved = (await patchMutate({
          id: initialRecommendation._id,
          partial: payload,
        })) as EnrichedRecommendationDoc;
        WebAnalytics.track(EVENT_ENRICHED_REC_EDITED, {
          recommendation_id: saved._id,
          location_id: locationId,
        });
        toast.success('Recommendation updated.');
        onRecommended?.(saved);
        onClose();
      } else {
        const created = (await upsertMutate(payload)) as EnrichedRecommendationItem;
        saved = created.recommendation;
        debug('[RecommendThisPlaceModal] create success', { recommendationId: saved._id, locationId });
        WebAnalytics.track(EVENT_ENRICHED_REC_SUBMITTED, {
          recommendation_id: saved._id,
          location_id: locationId,
        });
        onRecommended?.(saved);
        if (skipSaveOverlay) {
          // Parent owns the next step (e.g. Create New Link success) — do not onClose here.
          return;
        }
        const placeCard: PlaceCardData = {
          id: locationId ?? '',
          title: location?.name ?? 'This place',
          locationId: locationId ?? undefined,
          imageUrls: heroPhotoSrc ? [heroPhotoSrc] : undefined,
        };
        setSelectedPlaceForSave(placeCard);
        setSaveOverlayOpen(true);
        if (user) {
          void fetchUserItinerariesForSave();
          void fetchUserListsForSave();
        }
      }
    } catch {
      // useMutation already shows error toast
    }
  }, [
    mode,
    externalLink,
    locationId,
    notes,
    selectedTags,
    photoUrls,
    initialRecommendation,
    patchMutate,
    upsertMutate,
    onRecommended,
    onClose,
    validateLink,
    location,
    heroPhotoSrc,
    user,
    fetchUserItinerariesForSave,
    fetchUserListsForSave,
    skipSaveOverlay,
  ]);

  if (!isOpen) return null;

  // Share landing: no "Recommendation" sheet while loading — just a spinner until the place opens.
  if (openPlaceDetailsDirectly && viewLoadState?.status === 'loading') {
    return (
      <ResponsiveOverlay
        onClose={() => requestCloseView('backdrop')}
        presentation="dialog"
        unwrapped
        aria-label="Loading place"
      >
        <div role="status" aria-live="polite">
          <LoadingSpinner size="medium" color="primary" />
        </div>
      </ResponsiveOverlay>
    );
  }

  // Share landing goes straight to the place; closing it dismisses the whole share view.
  if (openPlaceDetailsDirectly && viewLoadState?.status === 'loaded' && locationId) {
    return (
      <CombinedContentPlaceModal
        isOpen
        onClose={() => requestCloseView('close')}
        locationId={locationId}
        locationData={locationData}
        entryPoint="profile_share_landing"
      />
    );
  }

  // ── Shared close-button SVG ──────────────────────────────────────────────
  const closeSvg = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <g clipPath={`url(#${closeIconClipId})`}>
        <rect
          x="2.45508"
          y="19.4258"
          width="24"
          height="2.99816"
          rx="1.49908"
          transform="rotate(-45 2.45508 19.4258)"
          fill="#252528"
        />
        <rect
          x="4.57422"
          y="2.45508"
          width="24"
          height="2.99907"
          rx="1.49954"
          transform="rotate(45 4.57422 2.45508)"
          fill="#252528"
        />
      </g>
      <defs>
        <clipPath id={closeIconClipId}>
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  // ── Shared hero block ────────────────────────────────────────────────────
  const heroBlock = (
    <div className={mode !== 'view' ? 'mb-4 mt-0' : undefined}>
      {mode !== 'view' && (
        <p className="mb-2 text-sm font-medium leading-snug text-gray-700">
          You&apos;re recommending
        </p>
      )}
      {/* Hero image — interactive (opens place details) except when already inside
          CombinedContentPlaceModal, where clicking would cause a stacking conflict and
          the user already has all the place context they need. */}
      {hidePlaceLink ? (
        /* Non-interactive: just shows the hero card, no click handler */
        <div className="block w-full border-0 bg-transparent p-0 text-left">
          <div className="relative h-[215px] w-full overflow-hidden rounded-card bg-gray-200 shadow-card">
            <div className="absolute inset-0">
              <CustomImage
                src={heroPhotoSrc}
                fallbackSrc={DEFAULT_POST_IMAGE}
                alt=""
                fill
                className="object-cover"
                lazy={false}
                aria-hidden
              />
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex flex-col rounded-b-card bg-[rgba(0,0,0,0.35)] px-4 py-3 shadow-card-overlay backdrop-blur-[20px]">
              <div className="flex w-full min-w-0 flex-col gap-1">
                <div className="flex min-w-0 items-center gap-2">
                  <RestaurantIcon
                    className="shrink-0 text-white drop-shadow-sm"
                    sx={{ fontSize: 24, color: '#fff' }}
                    aria-hidden
                  />
                  <p className="m-0 min-w-0 flex-1 line-clamp-2 [font-family:var(--font-lora,'Lora',Georgia,serif)] text-[22px] font-semibold leading-7 tracking-[0.02em] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                    {locationName}
                  </p>
                </div>
                {typePills.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {typePills.map((pill) => (
                      <span
                        key={pill}
                        className="inline-flex items-center justify-center rounded-card bg-orange-100 px-2 py-1 font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-gray-900"
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Interactive: tapping opens CombinedContentPlaceModal for more place context */
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setPlaceDetailsOpen(true);
          }}
          className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left outline-none ring-offset-2 transition hover:opacity-[0.97] focus-visible:ring-2 focus-visible:ring-orange-400/80"
          aria-label={`View place details for ${locationName}`}
        >
          <div className="relative h-[215px] w-full overflow-hidden rounded-card bg-gray-200 shadow-card">
            <div className="absolute inset-0">
              <CustomImage
                src={heroPhotoSrc}
                fallbackSrc={DEFAULT_POST_IMAGE}
                alt={mode !== 'view' ? '' : locationName}
                fill
                className="object-cover"
                lazy={false}
                aria-hidden={mode !== 'view'}
              />
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex flex-col rounded-b-card bg-[rgba(0,0,0,0.35)] px-4 py-3 shadow-card-overlay backdrop-blur-[20px]">
              <div className="flex w-full min-w-0 flex-col gap-1">
                <div className="flex min-w-0 items-center gap-2">
                  <RestaurantIcon
                    className="shrink-0 text-white drop-shadow-sm"
                    sx={{ fontSize: 24, color: '#fff' }}
                    aria-hidden
                  />
                  <p className="m-0 min-w-0 flex-1 line-clamp-2 [font-family:var(--font-lora,'Lora',Georgia,serif)] text-[22px] font-semibold leading-7 tracking-[0.02em] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                    {locationName}
                  </p>
                </div>
                {typePills.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {typePills.map((pill) => (
                      <span
                        key={pill}
                        className="inline-flex items-center justify-center rounded-card bg-orange-100 px-2 py-1 font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-gray-900"
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </button>
      )}
    </div>
  );

  const modal = (
    <ResponsiveOverlay
      onClose={handleOverlayClose}
      presentation="sheet"
      unwrapped
      className="gt-recommend-this-place-overlay"
      keyboardAware
    >
      <div
        className="gt-recommend-this-place-panel mobile-sheet relative z-10 flex min-h-0 w-full flex-col overflow-hidden bg-white font-manrope min-[768px]:rounded-3xl rounded-t-3xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'view' ? 'Recommendation' : 'Recommend This Place'}
      >
        {/* Header */}
        <div className="relative shrink-0 px-6 pt-5 pb-4">
          <button
            type="button"
            onClick={mode === 'view' ? () => requestCloseView('close') : handleClose}
            className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:opacity-80"
            aria-label="Close"
          >
            {closeSvg}
          </button>
          <h2 className="m-0 pr-11 text-center text-[20px] font-semibold leading-[1.28] tracking-[0.02em] text-[#111827] min-[768px]:text-[22px] [font-family:var(--font-lora,'Lora',Georgia,serif)]">
            {mode === 'view' ? 'Recommendation' : 'Recommend This Place'}
          </h2>
        </div>

        {/* Scrollable body */}
        <div ref={setScrollBodyRef} className="gt-keyboard-scroll min-h-0 flex-1 overflow-y-auto px-6 pb-4">

          {/* ── VIEW MODE: loading / error ──────────────────────────────── */}
          {mode === 'view' && viewLoadState?.status === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 py-10">
              <LoadingSpinner size="medium" color="primary" />
            </div>
          )}
          {mode === 'view' && viewLoadState?.status === 'error' && (
            <div className="flex flex-col items-center justify-center gap-3 py-10">
              <p className="text-center text-sm text-gray-500">{viewLoadState.message}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="text-sm text-indigo-700 underline"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {/* ── CONTENT: always in create/edit; when loaded in view ──────── */}
          {(mode !== 'view' || viewNormalized) && (
            <div className="space-y-4">

              {/* VIEW MODE: recommender avatar row */}
              {mode === 'view' && viewNormalized && (() => {
                const { recommender } = viewNormalized;
                const avatar = recommender.small_avatar_url || recommender.large_avatar_url || null;
                return (
                  <div className="flex items-center gap-3">
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-gray-200">
                      {avatar ? (
                        <Image
                          src={avatar}
                          alt={recommender.name}
                          fill
                          className="object-cover"
                          sizes="40px"
                        />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-sm font-semibold text-gray-500">
                          {recommender.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="m-0 text-xs leading-snug text-gray-400">Recommended by</p>
                      <p className="m-0 text-sm font-semibold leading-snug text-gray-900">
                        {recommender.name}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Hero — all modes */}
              {heroBlock}

              {/* VIEW MODE: missing place warning */}
              {mode === 'view' && viewNormalized && !viewNormalized.location && (
                <p className="text-xs text-amber-700">
                  The place record is no longer available, but you can still read this
                  recommendation below.
                </p>
              )}

              {/* Notes */}
              {mode !== 'view' ? (
                <div>
                  <label
                    className="mb-2 block font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-gray-900"
                    htmlFor="rec-this-place-notes"
                  >
                    Why do you recommend this place?
                  </label>
                  <textarea
                    id="rec-this-place-notes"
                    ref={notesRef}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onFocus={() => scrollFocusedIntoView()}
                    placeholder="Tell other travelers about your experience!"
                    maxLength={MAX_NOTES_LENGTH}
                    rows={4}
                    className="min-h-[120px] w-full resize-none rounded-card border border-gray-200 bg-[#F8F8FF] p-2 font-manrope text-sm font-normal leading-5 tracking-[0.02em] text-gray-900 placeholder:text-gray-500 focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200/60"
                  />
                </div>
              ) : (
                viewNormalized?.recommendation?.notes ? (
                  <div>
                    <p className="mb-2 font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-gray-900">
                      Why they recommend this place
                    </p>
                    <div className="min-h-[80px] w-full rounded-card border border-gray-200 bg-[#F8F8FF] p-2 font-manrope text-sm font-normal leading-5 tracking-[0.02em] text-gray-900 whitespace-pre-wrap">
                      {viewNormalized.recommendation.notes}
                    </div>
                  </div>
                ) : null
              )}

              {/* Tags */}
              {mode !== 'view' ? (
                <div>
                  <h3 className="mb-2 font-lora text-lg font-semibold text-gray-900">Tags</h3>
                  <div className="grid grid-cols-3 gap-2.5">
                    {RECOMMENDATION_TRAVEL_TAGS.map((tag) => {
                      const active = selectedTags.has(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.id)}
                          className={`w-full justify-center ${preferencePillClass(active)}`}
                          style={preferencePillStyle(active)}
                        >
                          <RecommendationPreferenceTagIcon tagId={tag.id} />
                          <span className="min-w-0 truncate">{tag.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                (viewNormalized?.recommendation?.travel_tags?.length ?? 0) > 0 ? (
                  <div>
                    <h3 className="mb-2 font-lora text-lg font-semibold text-gray-900">Tags</h3>
                    <div className="grid grid-cols-3 gap-2.5">
                      {(viewNormalized?.recommendation?.travel_tags ?? []).map((rawId) => {
                        const key = String(rawId).toLowerCase().trim();
                        const def = RECOMMENDATION_TRAVEL_TAGS.find((t) => t.id === key);
                        return (
                          <span
                            key={`${key}-${def?.label ?? rawId}`}
                            className={`w-full justify-center cursor-default ${preferencePillClass(true)}`}
                            style={preferencePillStyle(true)}
                          >
                            <RecommendationPreferenceTagIcon tagId={def?.id ?? key} />
                            <span className="min-w-0 truncate">{def?.label ?? String(rawId)}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ) : null
              )}

              {/* Photos */}
              {mode !== 'view' ? (
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-900">Photos</p>
                  {photoUrls.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {photoUrls.map((url) => (
                        <div
                          key={url}
                          className="relative h-20 w-20 overflow-hidden rounded-xl"
                        >
                          <Image
                            src={url}
                            alt="Uploaded"
                            fill
                            className="object-cover"
                            sizes="80px"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(url)}
                            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-sm leading-none text-white transition-colors hover:bg-black/80"
                            aria-label="Remove photo"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {photoUrls.length < MAX_PHOTOS && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handlePhotoFilesChange}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhotos}
                        className="inline-flex h-9 min-h-9 max-w-full items-center justify-center gap-2 rounded-button-md bg-white px-4 py-2 font-manrope text-sm font-normal leading-5 text-gray-900 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.15)] transition-[background-color,opacity] hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-900 disabled:pointer-events-none disabled:opacity-50"
                        style={{ border: '1px solid #252528' }}
                      >
                        <PhotoCameraOutlinedIcon sx={{ fontSize: 20, color: '#252528' }} aria-hidden />
                        {uploadingPhotos ? 'Uploading…' : 'Add photos'}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                viewUserPhotoUrls.length > 0 ? (
                  <div>
                    <p className="mb-2 text-sm font-semibold text-gray-900">Photos</p>
                    <button
                      type="button"
                      onClick={() => setViewPhotosOpen(true)}
                      className="inline-flex h-9 min-h-9 max-w-full items-center justify-center gap-2 rounded-button-md bg-white px-4 py-2 font-manrope text-sm font-normal leading-5 text-gray-900 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.15)] transition-[background-color,opacity] hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-900"
                      style={{ border: '1px solid #252528' }}
                      aria-haspopup="dialog"
                      aria-expanded={viewPhotosOpen}
                    >
                      <PhotoLibraryOutlinedIcon sx={{ fontSize: 20, color: '#252528' }} aria-hidden />
                      Photos
                    </button>
                  </div>
                ) : null
              )}

              {/* Link */}
              {mode !== 'view' ? (
                <div>
                  <label
                    className="mb-2 block font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-gray-900"
                    htmlFor="rec-this-place-link"
                  >
                    Add Link
                  </label>
                  <input
                    id="rec-this-place-link"
                    type="url"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    value={externalLink}
                    onChange={(e) => {
                      setExternalLink(e.target.value);
                      if (linkError) validateLink(e.target.value);
                    }}
                    onFocus={() => scrollFocusedIntoView()}
                    onBlur={(e) => validateLink(e.target.value)}
                    placeholder="Tiktok or Instagram link"
                    className={`w-full rounded-card border bg-[rgba(255,255,255,0.9)] px-5 py-3.5 font-manrope text-base font-normal leading-[22px] tracking-[0.02em] text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-200/60 ${
                      linkError
                        ? 'border-red-400 focus:border-red-400'
                        : 'border-gray-500 focus:border-orange-300'
                    }`}
                  />
                  {linkError && <p className="mt-1 text-xs text-red-500">{linkError}</p>}
                </div>
              ) : (
                (() => {
                  const link = viewNormalized?.recommendation?.external_link?.trim() ?? '';
                  return link ? (
                    <div>
                      <p className="mb-2 font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-gray-900">
                        Link
                      </p>
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={link}
                        className="block w-full truncate rounded-card border border-gray-500 bg-[rgba(255,255,255,0.9)] px-5 py-3.5 font-manrope text-base font-normal leading-[22px] tracking-[0.02em] text-blue-600 underline underline-offset-2 transition-colors hover:text-blue-800"
                      >
                        {link}
                      </a>
                    </div>
                  ) : null;
                })()
              )}

              {/* Submit button — create/edit only */}
              {mode !== 'view' && (
                <div className="mt-4 flex justify-end pb-2">
                  <button
                    type="button"
                    onClick={() => { void handleSubmit(); }}
                    disabled={isSubmitting || uploadingPhotos || linkBlocksSubmit}
                    className={`inline-flex h-9 min-h-9 items-center justify-center rounded-button-md px-4 font-manrope text-sm font-bold leading-5 tracking-[0.02em] text-white shadow-[0px_0px_20px_0px_rgba(0,0,0,0.15)] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-900 disabled:cursor-not-allowed disabled:opacity-50 ${
                      mode === 'edit'
                        ? 'bg-gray-900 hover:bg-gray-800'
                        : 'bg-orange-500 hover:bg-orange-500-hover'
                    }`}
                  >
                    {isSubmitting
                      ? 'Saving…'
                      : mode === 'edit'
                        ? 'Save changes'
                        : submitLabel}
                  </button>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </ResponsiveOverlay>
  );

  return (
    <>
      {modal}
      <SaveToOverlay
        isOpen={saveOverlayOpen}
        onClose={() => {
          setSaveOverlayOpen(false);
          onClose();
        }}
        onSaveToItinerary={handleSaveToItinerary}
        savedItineraries={userItineraries}
        place={selectedPlaceForSave}
        isLoading={isLoadingItineraries}
        savedLists={userLists}
        isLoadingLists={isLoadingLists}
        onSaveToList={handleSaveToList}
        onNewTripOrListCreated={handleNewTripOrListCreated}
        title="Also add to trip or list!"
        preOpenBanner="Added to your recommendations!"
        linkPreOpenBannerRecommendations
        overlayClassName="modal-overlay--above-recommend"
      />
      {placeDetailsOpen && (
        <CombinedContentPlaceModal
          isOpen
          onClose={() => setPlaceDetailsOpen(false)}
          locationId={locationId || undefined}
          locationData={locationData}
          overlayClassName="modal-overlay--above-recommend"
        />
      )}
      {mode === 'view' &&
        viewPhotosOpen &&
        viewUserPhotoUrls.length > 0 && (
          <ResponsiveOverlay
            onClose={() => setViewPhotosOpen(false)}
            presentation="sheet"
            unwrapped
            className="gt-recommend-this-place-photos-overlay"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Uploaded photos"
              className="gt-recommend-this-place-photos-panel mobile-sheet relative z-10 flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl min-[768px]:rounded-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-3">
                <h2 className="m-0 font-manrope text-base font-semibold text-gray-900">Photos</h2>
                <button
                  type="button"
                  onClick={() => setViewPhotosOpen(false)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100"
                  aria-label="Close photos"
                >
                  <span className="text-2xl leading-none text-gray-700" aria-hidden>
                    ×
                  </span>
                </button>
              </div>
              <div className="gt-keyboard-scroll min-h-0 flex-1 overflow-y-auto">
                <PlaceModalPhotosTab photoUrls={viewUserPhotoUrls} />
              </div>
            </div>
          </ResponsiveOverlay>
        )}
    </>
  );
};

export default RecommendThisPlaceModal;
